<template>
  <div class="qrcode-page" id="qrcode-capture">
    <header class="qrcode-header">
      <van-icon name="arrow-left" class="back-button" @click="goBack" />
      <span>分享问卷</span>
    </header>

    <main class="qrcode-card">
      <p class="qrcode-hint">客户使用微信扫码即可填写问卷</p>

      <div class="qrcode-image-wrap">
        <p v-if="loadingLink" role="status">正在生成问卷链接...</p>
        <div v-else-if="linkError" class="qrcode-error" role="alert">
          <p>{{ linkError }}</p>
          <button type="button" class="action-button" @click="bootstrap">重新生成</button>
        </div>
        <img
          v-if="qrImageUrl"
          ref="qrImage"
          :src="qrImageUrl"
          alt="问卷二维码"
          class="qrcode-image"
          @load="onQrLoaded"
          @error="onQrError"
        />
        <p v-if="qrLoadFailed" class="qrcode-error">二维码加载失败，请刷新页面重试</p>
      </div>

      <div class="link-block">
        <p class="link-label">链接地址</p>
        <p class="link-content">{{ link }}</p>
      </div>

      <div class="action-row">
        <button class="action-button" :disabled="loadingLink || !link" @click="copyLink">
          <van-icon name="link-o" /> 复制链接
        </button>
        <button class="action-button" :disabled="buildingPoster || !link" @click="downloadQr">
          <van-icon name="down" /> {{ buildingPoster ? '生成中…' : '下载分享图' }}
        </button>
      </div>

      <p class="capture-tip">
        下载分享图包含问卷说明与二维码，保存后可直接分享给客户。
      </p>
    </main>

    <p class="footer-tip">
      请务必提示并核验客户所填信息的真实性<span v-if="showReimbursementInfo">，信息不实将直接影响费用报销流程</span>。
    </p>

    <van-popup v-model="showPoster" round class="poster-popup" :close-on-click-overlay="false">
      <div class="poster-header"><strong>问卷分享图</strong><button type="button" aria-label="关闭分享图" @click="showPoster = false">关闭</button></div>
      <div class="poster-image-wrap"><img v-if="posterUrl" :src="posterUrl" alt="三高修复健康评估分享图，含扫码填写说明及问卷二维码" class="poster-image" /></div>
      <p class="poster-save-tip">{{ inWechat ? '请长按上方图片，保存到手机或发送给客户' : '可保存图片分享给客户；手机也可长按图片保存' }}</p>
      <button v-if="!inWechat" type="button" class="action-button poster-save-button" @click="savePoster">保存分享图</button>
    </van-popup>
  </div>
</template>

<script>
import { Toast } from "vant";
import {
  SAN_GAO_QUESTIONNAIRE_BASE_URL,
  buildQrImageUrl,
  canShowExamReimbursement,
  isTaikangOrganization,
} from "@/config/sanGao";
import { getSanGaoSalesmanInfo, getSanGaoOrgById } from "@/api/sanGao";
import { buildQuestionnairePoster } from "./questionnaire-poster";

const STORAGE_KEYS = {
  token: "san_gao_salesman_token",
  info: "san_gao_salesman_info",
  orgId: "san_gao_salesman_orgId",
};

export default {
  name: "SanGaoSalesmanQrcode",
  data() {
    return {
      salesmanId: null,
      salesmanCode: "",
      orgId: "",
      orgInfo: null,
      loadingLink: false,
      linkError: "",
      link: "",
      qrImageUrl: "",
      qrLoadFailed: false,
      buildingPoster: false,
      showPoster: false,
      posterUrl: "",
      posterLink: "",
      inWechat: /MicroMessenger/i.test(navigator.userAgent),
    };
  },
  computed: {
    showReimbursementInfo() {
      return canShowExamReimbursement(this.orgInfo);
    },
  },
  created() {
    this.orgId = this.$route.query.orgId || localStorage.getItem(STORAGE_KEYS.orgId) || "";
    const routeSalesmanId = this.$route.query.salesmanId;
    if (routeSalesmanId) {
      this.salesmanId = String(routeSalesmanId);
    }
    this.bootstrap();
  },
  methods: {
    async loadOrgPolicy() {
      const orgId = this.orgId;
      if (!orgId) return false;
      try {
        const res = await getSanGaoOrgById({ orgId });
        if (this._isDestroyed || String(this.orgId) !== String(orgId)) return false;
        if (res && res.code === 200 && res.data && String(res.data.orgId) === String(orgId) &&
            Object.prototype.hasOwnProperty.call(res.data, "companyId")) {
          this.orgInfo = res.data;
          return true;
        }
      } catch (e) {
        // 不能确定保司时不生成链接，避免泰康误分享未带 masked=1 的问卷。
      }
      return false;
    },
    async bootstrap() {
      if (this.loadingLink) return;
      this.loadingLink = true;
      this.linkError = "";
      this.link = "";
      this.qrImageUrl = "";
      this.qrLoadFailed = false;
      this.orgInfo = null;
      if (!this.salesmanId) {
        const cached = localStorage.getItem(STORAGE_KEYS.info);
        if (cached) {
          try {
            const info = JSON.parse(cached);
            this.salesmanId = info.id ? String(info.id) : null;
            this.salesmanCode = info.salesmanCode || "";
          } catch (e) {
            // ignore
          }
        }
      }
      if (!this.salesmanId) {
        try {
          const res = await getSanGaoSalesmanInfo();
          if (res && res.code === 200 && res.data) {
            this.salesmanId = res.data.id ? String(res.data.id) : null;
            this.salesmanCode = res.data.salesmanCode || "";
            localStorage.setItem(STORAGE_KEYS.info, JSON.stringify(res.data));
          }
        } catch (e) {
          // ignore
        }
      }
      if (!this.salesmanId) {
        this.linkError = "业务员信息加载失败，请重试";
        this.loadingLink = false;
        return;
      }
      try {
        // 先确定机构所属保司，再生成页面二维码、复制链接及分享图所用的统一链接。
        if (!await this.loadOrgPolicy()) {
          this.linkError = "机构信息加载失败，暂无法生成问卷链接，请重试";
          return;
        }
        if (!this._isDestroyed) await this.buildLink();
      } finally {
        this.loadingLink = false;
      }
    },
    async buildLink() {
      const params = `salesmanId=${encodeURIComponent(this.salesmanId || "")}&orgId=${encodeURIComponent(this.orgId || "")}${isTaikangOrganization(this.orgInfo) ? "&masked=1" : ""}`;
      // 目标页面是 hash 路由 SPA，参数必须拼在 # 路径之后，Vue Router 才能从 $route.query 读到。
      const hashIdx = SAN_GAO_QUESTIONNAIRE_BASE_URL.indexOf("#");
      if (hashIdx >= 0) {
        const beforeHash = SAN_GAO_QUESTIONNAIRE_BASE_URL.slice(0, hashIdx);
        const afterHash = SAN_GAO_QUESTIONNAIRE_BASE_URL.slice(hashIdx + 1);
        const sep = afterHash.indexOf("?") >= 0 ? "&" : "?";
        this.link = `${beforeHash}#${afterHash}${sep}${params}`;
      } else {
        const sep = SAN_GAO_QUESTIONNAIRE_BASE_URL.indexOf("?") >= 0 ? "&" : "?";
        this.link = `${SAN_GAO_QUESTIONNAIRE_BASE_URL}${sep}${params}`;
      }
      this.qrImageUrl = await buildQrImageUrl(this.link, 320);
      this.qrLoadFailed = !this.qrImageUrl;
    },
    onQrLoaded() {
      this.qrLoadFailed = false;
    },
    onQrError() {
      this.qrLoadFailed = true;
    },
    async copyLink() {
      if (!this.link) {
        Toast("链接未生成");
        return;
      }
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(this.link);
        } else {
          const input = document.createElement("input");
          input.value = this.link;
          document.body.appendChild(input);
          input.select();
          document.execCommand("copy");
          document.body.removeChild(input);
        }
        Toast("链接已复制");
      } catch (e) {
        Toast("复制失败，请长按链接手动复制");
      }
    },
    async downloadQr() {
      if (this.buildingPoster) return;
      if (!this.link) {
        Toast("链接未生成");
        return;
      }
      this.buildingPoster = true;
      try {
        const link = this.link;
        if (!this.posterUrl || this.posterLink !== link) {
          const image = await buildQuestionnairePoster(link);
          if (this._isDestroyed || this.link !== link) return;
          this.posterUrl = image;
          this.posterLink = link;
        }
        this.showPoster = true;
        // 微信内以图片长按保存为主，避免 download 被微信拦截后打开空白页。
        if (!this.inWechat) this.savePoster();
      } catch (e) {
        Toast("分享图生成失败，请重试");
      } finally {
        this.buildingPoster = false;
      }
    },
    savePoster() {
      if (!this.posterUrl || this.posterLink !== this.link) {
        Toast("请先重新生成分享图");
        return;
      }
      const a = document.createElement("a");
      a.href = this.posterUrl;
      a.download = `三高修复_健康问卷分享图_${this.salesmanCode || this.salesmanId}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    },
    goBack() {
      this.$router.back();
    },
  },
};
</script>

<style lang="less" scoped>
.qrcode-page {
  position: relative;
  min-height: 100vh;
  padding: 0 0 32px;
  background: linear-gradient(180deg, #0874ca 0%, #4ba8e0 26%, #dff6ff 56%, #f4fafd 100%);
  color: #163b5c;
}

.qrcode-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  text-shadow: 0 2px 6px rgba(0, 48, 113, 0.3);
}

.back-button {
  position: absolute;
  top: 50%;
  left: 14px;
  font-size: 20px;
  color: #fff;
  cursor: pointer;
  transform: translateY(-50%);
}

.qrcode-card {
  margin: 16px;
  padding: 22px 18px 18px;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(12, 91, 147, 0.12);
  text-align: center;
}

.qrcode-hint {
  margin: 0 0 14px;
  font-size: 13px;
  color: #5b7488;
}

.qrcode-image-wrap {
  position: relative;
  display: inline-block;
  margin: 0 auto 16px;
  padding: 10px;
  border: 1px solid #eef4f9;
  border-radius: 12px;
  background: #fafdff;
}

.qrcode-image {
  display: block;
  width: 220px;
  height: 220px;
}

.qrcode-error {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 14px;
  font-size: 12px;
  color: #b14545;
  background: #fde0e0;
  border-radius: 12px;
}

.link-block {
  padding: 12px 12px;
  margin-bottom: 14px;
  border-radius: 10px;
  text-align: left;
  background: #f4fafd;
}

.link-label {
  margin: 0 0 4px;
  font-size: 11px;
  color: #7c8d9c;
}

.link-content {
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
  word-break: break-all;
  color: #163b5c;
}

.action-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 12px;
}

.action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 42px;
  border: 0;
  border-radius: 21px;
  font-size: 14px;
  font-weight: 500;
  color: #fff;
  background: linear-gradient(100deg, #19a2e5, #146bd2);
  box-shadow: 0 4px 12px rgba(17, 112, 199, 0.18);
  cursor: pointer;
}

.action-button .van-icon {
  margin-right: 6px;
  font-size: 16px;
}

.action-button:active {
  opacity: 0.85;
}

.action-button:disabled { opacity: 0.55; cursor: default; }

.poster-popup {
  display: flex;
  flex-direction: column;
  width: 88%;
  max-width: 380px;
  max-height: 92vh;
  padding: 0 14px 16px;
  box-sizing: border-box;
  background: #f4f8fc;
}
.poster-header {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  min-height: 48px;
  font-size: 15px;
  button { min-width: 44px; min-height: 44px; padding: 0; border: 0; color: #607b90; background: transparent; font-size: 13px; }
}
.poster-image-wrap { min-height: 0; overflow-y: auto; text-align: center; }
.poster-image { display: block; width: 100%; height: auto; -webkit-touch-callout: default; user-select: auto; }
.poster-save-tip { flex-shrink: 0; margin: 12px 0 0; font-size: 12px; line-height: 1.6; color: #5b7488; text-align: center; }
.poster-save-button { flex-shrink: 0; width: 100%; margin-top: 12px; }

.capture-tip {
  margin: 6px 0 0;
  font-size: 11px;
  color: #7c8d9c;
}

.footer-tip {
  margin: 18px 22px 0;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.8;
  color: #163b5c;
  text-align: center;
}
</style>
